import React, { useState } from "react";
import { Box, TextField, DialogTitle, DialogActions, Button, DialogContent, Dialog, InputLabel, FormControl, FormHelperText, OutlinedInput } from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { formatDate } from "../../../../utilities/formatters";
import { preventDialogBackdropClick } from "../../../../utilities/generators";

function DevelopmentNeedFormDialog({open, formTitle, submitLabel = "Save", onSave, onClose, data = {}}) {
    const [title, setTitle] = useState(data.title || "");
    const [description, setDescription] = useState(data.description || "");
    const [dateCompletion, setDateCompletion] = useState(data.dateCompletion || "");
    const [dateReview, setDateReview] = useState(data.dateReview || "");
    const [planOfAction, setPlanOfAction] = useState(data?.planOfAction || "");
    const [proposedOutcome, setProposedOutcome] = useState(data?.proposedOutcome || "");
    const [resourcesRequired, setResourcesRequired] = useState(data?.resourcesRequired || "");

    const onSubmit = () => {
        const params = {
            ...data,
            title,
            description,
            dateCompletion: formatDate(dateCompletion),
            dateReview: formatDate(dateReview),
            planOfAction,
            proposedOutcome,
            resourcesRequired,
        };
        onSave(params);
        onClose();
    };

    return (
        <Dialog open={!!open} onClose={preventDialogBackdropClick(onClose)} fullWidth={true} maxWidth="lg">
            <DialogTitle>{formTitle || "New development need"}</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth
                        required
                        error={!title}
                    >
                        <InputLabel
                            id="label-title"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Title
                        </InputLabel>
                        <OutlinedInput
                            id="text-title"
                            placeholder="Title"
                            autoFocus
                            value={title}
                            onChange={({target}) => setTitle(target.value)}
                            inputProps={{ maxLength: 250 }}
                        />
                        <FormHelperText>Add a title</FormHelperText>
                    </FormControl>

                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth
                        required
                        error={!description}
                    >
                        <InputLabel
                            id="label-description"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Description
                        </InputLabel>
                        <OutlinedInput
                            id="text-description"
                            placeholder="Description"
                            value={description}
                            onChange={({target}) => setDescription(target.value)}
                            inputProps={{ maxLength: 255 }}
                            multiline
                            rows={2}
                        />
                        <FormHelperText>
                            Explain the development need (max 255 characters)
                        </FormHelperText>
                    </FormControl>

                    <Box className="flex-container">
                        <FormControl component="div" margin="normal">
                            <DatePicker
                                label="Review"
                                value={!!dateReview ? dayjs(dateReview) : null}
                                format="DD/MM/YYYY"
                                onChange={setDateReview}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FormControl>
                        <FormControl component="div" margin="normal">
                            <DatePicker
                                label="Completion"
                                value={!!dateCompletion ? dayjs(dateCompletion) : null}
                                format="DD/MM/YYYY"
                                onChange={setDateCompletion}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FormControl>
                    </Box>

                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth
                        required
                        error={!planOfAction}
                    >
                        <InputLabel
                            id="label-planOfAction"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Plan of action
                        </InputLabel>
                        <OutlinedInput
                            id="text-planOfAction"
                            placeholder="Plan"
                            value={planOfAction}
                            onChange={({target}) => setPlanOfAction(target.value)}
                            multiline
                            rows={3}
                        />
                        <FormHelperText>
                            How will you address this development need?
                        </FormHelperText>
                    </FormControl>
                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth
                    >
                        <InputLabel
                            id="label-resourcesRequired"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Resources
                        </InputLabel>
                        <OutlinedInput
                            id="text-resourcesRequired"
                            placeholder="Resources"
                            value={resourcesRequired}
                            onChange={({target}) => setResourcesRequired(target.value)}
                            multiline
                            rows={3}
                        />
                        <FormHelperText>
                            What resources do you need (if any)?
                        </FormHelperText>
                    </FormControl>
                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth
                        required
                        error={!proposedOutcome}
                    >
                        <InputLabel
                            id="label-proposedOutcome"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Outcome
                        </InputLabel>
                        <OutlinedInput
                            id="text-proposedOutcome"
                            placeholder="Outcome"
                            value={proposedOutcome}
                            onChange={({target}) => setProposedOutcome(target.value)}
                            multiline
                            rows={3}
                        />
                        <FormHelperText>
                            How do you anticipate this will change your work?
                        </FormHelperText>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    key="save"
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={onSubmit}
                    disabled={!title || !description}
                >
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DevelopmentNeedFormDialog;
